import { useMemo } from "react";
import IconStarFill from "@/components/icons/icon-star-fill";

export interface StarRatingProps {
	/** Rating value (e.g., 4.2, 3.7) — supports fractional values */
	rating: number;
	/** Maximum number of stars */
	maxStars?: number;
	/** Size class for each star (Tailwind) */
	starClassName?: string;
	/** Color for filled stars */
	filledColor?: string;
	/** Color for empty stars */
	emptyColor?: string;
}

export default function StarRating({
	rating,
	maxStars = 5,
	starClassName = "w-7 h-7",
	filledColor,
	emptyColor,
}: StarRatingProps) {
	const clampedRating = Math.min(Math.max(rating, 0), maxStars);

	const stars = useMemo(() => {
		return Array.from({ length: maxStars }, (_, i) => {
			const fillPercent = Math.min(Math.max(clampedRating - i, 0), 1) * 100;
			return { index: i, fillPercent };
		});
	}, [clampedRating, maxStars]);

	return (
		<div className="flex gap-1">
			{stars.map(({ index, fillPercent }) => {
				if (fillPercent === 100) {
					return (
						<IconStarFill
							key={index}
							className={`${starClassName} ${filledColor ? "" : "text-amber-500"}`}
							style={filledColor ? { color: filledColor } : undefined}
						/>
					);
				}

				if (fillPercent === 0) {
					return (
						<IconStarFill
							key={index}
							className={`${starClassName} ${emptyColor ? "" : "text-gray-300"}`}
							style={emptyColor ? { color: emptyColor } : undefined}
						/>
					);
				}

				// Partial star using clipPath
				const clipId = `star-clip-${index}`;
				return (
					<span key={index} className={`relative inline-block ${starClassName}`}>
						<svg className="absolute inset-0 w-full h-full" aria-hidden="true">
							<defs>
								<clipPath id={clipId}>
									<rect x="0" y="0" width={`${fillPercent}%`} height="100%" />
								</clipPath>
							</defs>
						</svg>
						{/* Empty star (background) */}
						<IconStarFill
							className={`absolute inset-0 w-full h-full ${emptyColor ? "" : "text-gray-300"}`}
							style={emptyColor ? { color: emptyColor } : undefined}
						/>
						{/* Filled portion */}
						<IconStarFill
							className={`absolute inset-0 w-full h-full ${filledColor ? "" : "text-amber-500"}`}
							style={{
								clipPath: `inset(0 ${100 - fillPercent}% 0 0)`,
								...(filledColor ? { color: filledColor } : {}),
							}}
						/>
					</span>
				);
			})}
		</div>
	);
}
